import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

// Phase-status combobox for a forecast building row. The dropdown is portalled
// with fixed positioning so it escapes the weights table's overflow:auto.
const FilterableStatusSelect = ({ value, options, onChange, disabled }) => {
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const [pos, setPos]       = useState({ top: 0, left: 0, width: 0 });
  const containerRef        = useRef(null);

  const filtered = query.trim()
    ? options.filter((o) => o.name.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return;
    // Use fixed positioning so the dropdown escapes the table's overflow:auto
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });

    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (name) => { onChange(name); setQuery(''); setOpen(false); };
  const handleClear  = (e)    => { e.stopPropagation(); onChange(''); setQuery(''); };

  return (
    <div ref={containerRef} className="tw-relative tw-w-full">
      <div
        className={`tw-flex tw-items-center tw-w-full tw-border tw-rounded-sm tw-text-xs tw-px-1.5 tw-py-1 tw-gap-1 ${
          disabled ? 'tw-bg-gray-100 tw-cursor-not-allowed tw-border-gray-200' : 'tw-bg-white tw-cursor-pointer tw-border-gray-300'
        } ${open ? 'tw-border-blue-400' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        {open ? (
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => { e.stopPropagation(); setQuery(e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            placeholder="filter…"
            className="tw-flex-1 tw-border-0 tw-outline-none tw-text-xs tw-bg-transparent"
          />
        ) : (
          <span className={`tw-flex-1 tw-truncate ${value ? 'tw-text-gray-800' : 'tw-text-gray-400'}`}>
            {value || '— select —'}
          </span>
        )}
        {value && !disabled && !open && (
          <button type="button" onClick={handleClear}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-leading-none">
            ×
          </button>
        )}
        {!open && <span className="tw-text-gray-400 tw-text-xs tw-ml-0.5">▾</span>}
      </div>

      {open && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 99999 }}
          className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-sm tw-shadow-xl tw-max-h-52 tw-overflow-y-auto"
        >
          {filtered.length === 0 ? (
            <div className="tw-px-2 tw-py-1.5 tw-text-gray-400 tw-text-xs">No results</div>
          ) : (
            filtered.map((o) => (
              <div key={o.id} onMouseDown={() => handleSelect(o.name)}
                className={`tw-px-2 tw-py-1.5 tw-text-xs tw-cursor-pointer hover:tw-bg-blue-50 ${
                  value === o.name ? 'tw-bg-blue-100 tw-font-semibold tw-text-blue-700' : 'tw-text-gray-700'
                }`}
              >
                {o.name}
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

FilterableStatusSelect.propTypes = {
  value:    PropTypes.string,
  options:  PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default FilterableStatusSelect;
